import { Box } from '@lifeomic/chroma-react/components/Box';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { ButtonUnstyled } from '@lifeomic/chroma-react/components/ButtonUnstyled';
import { ExpansionPanel } from '@lifeomic/chroma-react/components/ExpansionPanel';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { ExternalLink } from '@lifeomic/chromicons';
import { lighten } from '@mui/material/styles';
import React, { useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useLocalStorageState } from '../../../../../hooks';
import { useActiveUser } from '../../../../../hooks/useActiveUser';
import { track as mixpanelTrack } from '../../../../../hooks/useMixpanel';
import { useReportErrorToBugsnag } from '../../../../../util/bugsnagReporting';
import { ImpressionItem } from '../../ExecutiveSummary/executive-summary';
import { footnoteRegexp } from '../../ExecutiveSummary/utils';
import { MedicalResultChartCondensed } from '../../Measurements/Graphs/Ranges/MedicalResultsChartCondensed/MedicalResultChartCondensed';
import { Sparkline } from '../../Measurements/Graphs/Ranges/Sparkline';
import { useAttachmentArbiter } from '../AttachmentArbiter';
import {
  CommunicationAttachment,
  DocumentReferenceAttachment,
  ObservationAttachment,
  PAGE_NUMBER_EXTENSION_URL,
  QuestionnaireResponseAttachment,
} from '../AttachmentArbiter/Attachment';
import { DocumentReferenceContent } from '../AttachmentArbiter/modals/DocumentReferenceModal';
import { useDocumentReferenceTracker } from '../RenderingTracking/trackers/documentReferenceTracker';
import { useObservationTracker } from '../RenderingTracking/trackers/observationTracker';
import { useQuestionnaireResponseTracker } from '../RenderingTracking/trackers/questionnaireResponseTracker';
import { parseKnowledgeBaseCitation } from '../../../../../util/knowledgeBaseCitation';
import { KnowledgeBaseCitationInline } from './KnowledgeBaseCitationInline';
import { AttachmentItem } from './AttachmentList';

export type ViewMode =
  | 'editing'
  | 'compact'
  | 'expanded'
  | 'presentation-expanded'
  | 'presentation-compact'
  | 'print';

/**
 * For compact and expanded view modes, we want to move the footnote references to the end of the block (i.e., double-newlines)
 * so that we can render them as widgets between paragraphs or between lists, but not between list items.
 * The regexp matches both the generated footnotes and the footnote references that get edited by the edit mode and
 * attachment arbiter.
 */

const moveRegexpToEndOfBlock = (text: string, regexp: RegExp): string => {
  return text
    .split('\n\n')
    .map(line => {
      if (line.trim().endsWith(':')) {
        return line;
      }
      const match = line.match(regexp);
      if (match) {
        // when we "collect" the footnotes at the end of the line, we also want to deduplicate them
        const moved = new Set<string>();
        match.forEach(footnote => {
          line = line.replace(footnote, '').trimEnd();
          moved.add(footnote.trim());
        });
        return line + '\n' + Array.from(moved).join(' ');
      }
      return line;
    })
    .join('\n\n');
};

export const getSummaryForViewMode = (
  summary: string,
  _viewMode: ViewMode,
): string => {
  const moved = moveRegexpToEndOfBlock(summary, footnoteRegexp);
  return moved.replace(/\n\n\n/g, '\n\n');
};

const useStyles = makeStyles(theme => ({
  footnote: {
    minWidth: 0,
    paddingLeft: 0,
    paddingRight: 0,
    fontWeight: 400,
    cursor: 'pointer',
    fontSize: theme.pxToRem(13),
    lineHeight: 0,
    verticalAlign: 'super',
    'a&': {
      textDecoration: 'none',
      '&:hover, &:focus': {
        textDecoration: 'underline',
      },
    },
  },
  border: {
    border: `1px solid ${theme.palette.divider}`,
  },
  attachment: {
    display: 'inline-block',

    '&:before': {
      content: '.',
      display: 'block',
    },
  },
  expansionPanel: {
    borderColor: theme.palette.divider,
    border: '1px solid',
    borderRadius: theme.pxToRem(4),
    overflow: 'hidden',
    margin: theme.spacing(1.5, 0),
    '& div': {
      boxShadow: 'none',
      background: lighten(theme.palette.graphite[900], 0.9),
    },
    '& button': {
      backgroundColor: 'transparent',
    },
    '@media print': {
      '&': {
        breakInside: 'avoid',
      },
    },
  },
  expansionPanelContent: {
    padding: 0,
    height: 600,
  },
  noMaxHeightContent: {
    maxHeight: 'unset !important',
  },
  smallExpansionPanelContent: {
    padding: 0,
    height: 'auto',
    backgroundColor: 'white',
  },
  externalLink: {
    paddingLeft: 0,
    paddingRight: 0,
  },
}));

const messages = defineMessages({
  documentReferenceAttachment: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.inlineAttachment.documentReferenceAttachment',
    defaultMessage: '{display} (Page {pageNumber})',
  },
  openInNewTab: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.inlineAttachment.openInNewTab',
    defaultMessage: 'Open in new tab',
  },
  questionnaireResponse: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.inlineAttachment.questionnaireResponse',
    defaultMessage: 'Questionnaire response',
  },
});

export const InlineAttachment = ({
  footnoteId,

  patient,
  isEditing,
  viewMode,
  impression,
  onDelete,
}: {
  footnoteId: string;
  text: string;
  patient: fhir.Patient;
  isEditing: boolean;
  viewMode: ViewMode;
  impression: ImpressionItem;
  onDelete: () => void;
}) => {
  const [cachedExpansionState, setCachedExpansionState] = useLocalStorageState<
    'expanded' | 'collapsed'
  >(
    `es-inline-attachment-expansion-${patient.id}-${impression.impressionType}-${footnoteId}`,
    viewMode === 'presentation-compact' ? 'collapsed' : 'expanded',
  );
  const activeUser = useActiveUser();
  const reportErrorToBugsnag = useReportErrorToBugsnag();
  const unsupportedReportedKeyRef = useRef<string | null>(null);
  const observationTracker = useObservationTracker();
  const documentReferenceTracker = useDocumentReferenceTracker();
  const questionnaireResponseTracker = useQuestionnaireResponseTracker();
  const effectiveViewMode = isEditing ? 'editing' : viewMode;
  const { attachmentMap, openAttachment } = useAttachmentArbiter();
  const attachment =
    impression.getDeduplicatedAttachmentByIdentifier(footnoteId);
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const isPrint = viewMode === 'print';
  // KB citations arrive in two shapes:
  //   1. Short agent-issued ID in the markdown footnote (e.g. `kb0`), with the
  //      S3 URI stored on the matching investigation item's `reference`.
  //   2. Raw `s3://…/.files-kb-…` URI inlined directly as the footnote body.
  // Resolving attachment-first lets us recover the URI for (1); falling back
  // to `footnoteId` preserves (2) for surfaces without an impression context.
  const knowledgeBaseCitation =
    (attachment?.reference
      ? parseKnowledgeBaseCitation(attachment.reference)
      : null) ?? parseKnowledgeBaseCitation(footnoteId);

  if (knowledgeBaseCitation) {
    return (
      <Box direction="row" marginTop={2} className={classes.attachment}>
        <KnowledgeBaseCitationInline citation={knowledgeBaseCitation} />
      </Box>
    );
  }

  if (!attachment) {
    return null;
  }

  const attachmentReference = attachmentMap[attachment.reference || ''];
  if (!attachmentReference) {
    return null;
  }

  if (effectiveViewMode === 'editing') {
    return (
      <Box direction="row" marginTop={2} className={classes.attachment}>
        <AttachmentItem
          attachment={attachment!}
          disableDeepLink={false}
          disableThumbnail={false}
          className={classes.border}
          action="edit"
          onUseAttachment={onDelete}
        />
      </Box>
    );
  }

  if (
    impression.impressionType === 'synopsis' &&
    attachmentReference instanceof ObservationAttachment
  ) {
    if (
      !observationTracker.shouldRender({
        attachment: attachmentReference,
        summaryType: impression.impressionType,
      })
    ) {
      return null;
    }
    return (
      <ObservationAttachmentInSparkline
        attachment={attachmentReference}
        attachmentReference={attachment}
        patient={patient}
      />
    );
  }

  if (attachmentReference instanceof CommunicationAttachment) {
    // Note: we don't render communications in the executive summary anymore
    // based on physician feedback (they don't want them in the AR because they're
    // not member-formatted/facing. They're good _citations_ but not attachments.)
    return null;
  }

  if (attachmentReference instanceof QuestionnaireResponseAttachment) {
    if (
      !questionnaireResponseTracker.shouldRender({
        attachment: attachmentReference,
      })
    ) {
      return null;
    }
    return (
      <Box direction="row" marginTop={2} className={classes.attachment}>
        <Button
          variant="outlined"
          title={attachmentReference.display}
          onClick={() => openAttachment(attachment)}
        >
          {formatMessage(messages.questionnaireResponse)}
        </Button>
      </Box>
    );
  }

  if (attachmentReference instanceof ObservationAttachment) {
    if (attachmentReference.primaryCode && attachmentReference.isBiomarker) {
      if (
        !observationTracker.shouldRender({
          attachment: attachmentReference,
          summaryType: impression.impressionType,
        })
      ) {
        return null;
      }
      return (
        <MedicalResultChartCondensed
          dimension={{
            codes: [attachmentReference.primaryCode],
          }}
          patient={patient}
          source={undefined} // todo: support source when AR might reference wearables data, or allow some kind of "any" source from history endpoint
        />
      );
    }
  }

  if (attachmentReference instanceof DocumentReferenceAttachment) {
    if (
      !documentReferenceTracker.shouldRender({
        attachment: attachmentReference,
        reference: attachment,
      })
    ) {
      return null;
    }
    if (effectiveViewMode === 'compact' || effectiveViewMode === 'print') {
      return (
        <Box direction="row" marginTop={2} className={classes.attachment}>
          <AttachmentItem
            attachment={attachment!}
            disableDeepLink={false}
            disableThumbnail={false}
            className={classes.border}
            action="readonly"
          />
        </Box>
      );
    }
    if (
      effectiveViewMode === 'expanded' ||
      effectiveViewMode === 'presentation-expanded' ||
      effectiveViewMode === 'presentation-compact'
    ) {
      return (
        <DocumentReferenceExpansion
          attachmentReference={attachmentReference}
          attachment={attachment}
          attachmentMap={attachmentMap}
          openAttachment={openAttachment}
          cachedExpansionState={cachedExpansionState}
          setCachedExpansionState={setCachedExpansionState}
          isPrint={isPrint}
          classes={classes}
        />
      );
    }
  }

  const dedupeKey = `${patient.id}|${footnoteId}|${attachment.reference ?? ''}`;
  if (unsupportedReportedKeyRef.current !== dedupeKey) {
    unsupportedReportedKeyRef.current = dedupeKey;
    const attachmentKind =
      attachmentReference.resource?.resourceType ?? 'unknown';
    const metadata = {
      footnoteId,
      impressionType: impression.impressionType,
      fhirReference: attachment.reference,
      attachmentKind,
    };
    reportErrorToBugsnag(
      new Error('unsupported inline attachment type'),
      metadata,
    );
    // useMixpanel().track skips delivery when Redux active user is not yet loaded.
    // Fall back to subject id so this diagnostic still reaches Mixpanel.
    const mixpanelUser =
      activeUser?.id ??
      (patient.id != null ? String(patient.id) : 'unknown-viewer');
    mixpanelTrack('unsupported_inline_attachment_type', {
      user: mixpanelUser,
      clinicianId: activeUser?.id,
      patientId: patient.id,
      ...metadata,
    });
  }
  return null;
};

function ObservationAttachmentInSparkline({
  attachment,
  attachmentReference,
  patient,
}: {
  attachment: ObservationAttachment;
  attachmentReference: fhir.Reference;
  patient: fhir.Patient;
}) {
  const { openAttachment } = useAttachmentArbiter();

  if (!attachment.primaryCode) {
    return null;
  }

  return (
    <ButtonUnstyled
      onClick={() => openAttachment(attachmentReference)}
      role="button"
      tabIndex={0}
      title={attachment.display || ''}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          openAttachment(attachmentReference);
        }
      }}
    >
      <Sparkline
        dimension={{
          codes: [attachment.primaryCode],
        }}
        patient={patient}
      />
    </ButtonUnstyled>
  );
}

type DocumentReferenceExpansionProps = {
  attachmentReference: DocumentReferenceAttachment;
  attachment: fhir.Reference;
  attachmentMap: Record<string, { display: string | undefined }>;
  openAttachment: (
    reference: fhir.Reference,
    options?: { disableDeepLink?: boolean },
  ) => void;
  cachedExpansionState: 'expanded' | 'collapsed';
  setCachedExpansionState: (next: 'expanded' | 'collapsed') => void;
  isPrint: boolean;
  classes: ReturnType<typeof useStyles>;
};

function DocumentReferenceExpansion({
  attachmentReference,
  attachment,
  attachmentMap,
  openAttachment,
  cachedExpansionState,
  setCachedExpansionState,
  isPrint,
  classes,
}: DocumentReferenceExpansionProps) {
  const { formatMessage } = useIntl();
  const pageNumber =
    attachment.extension?.find(ext => ext.url === PAGE_NUMBER_EXTENSION_URL)
      ?.valueInteger ?? 1;
  const attachmentData = attachmentMap[attachment.reference || ''];
  const title = formatMessage(messages.documentReferenceAttachment, {
    display: attachmentData?.display,
    pageNumber,
  });

  const content = (
    <DocumentReferenceContent
      attachment={attachmentReference}
      reference={attachment}
      disablePdfNav={true}
    />
  );

  if (attachmentReference.isImage()) {
    return content;
  }

  return (
    <ExpansionPanel
      title={title}
      isOpen={cachedExpansionState === 'expanded' || isPrint}
      onToggle={isExpanded => {
        setCachedExpansionState(isExpanded ? 'expanded' : 'collapsed');
      }}
      className={classes.expansionPanel}
      innerContentClassName={classes.expansionPanelContent}
      contentClassName={isPrint ? classes.noMaxHeightContent : ''}
      secondaryHeader={
        <Tooltip title={formatMessage(messages.openInNewTab)}>
          <IconButton
            data-testid="open-document-reference-button"
            aria-label={formatMessage(messages.openInNewTab)}
            className={classes.externalLink}
            icon={ExternalLink}
            size={0}
            onClick={() =>
              openAttachment(attachment, { disableDeepLink: false })
            }
          />
        </Tooltip>
      }
    >
      {content}
    </ExpansionPanel>
  );
}

const MemoizedInlineAttachment = React.memo(InlineAttachment);

export default MemoizedInlineAttachment;
